import React, { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, Clock, FileText, ArrowLeftRight, Thermometer } from 'lucide-react';
import { format, parseISO, getISOWeek } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatWeekends } from '../constants/weekendRhythm';
import WhistleIcon, { TRAINING_LABEL } from '../components/common/WhistleIcon';
import SickNoteToggle from '../components/common/SickNoteToggle';

function formatDate(dateStr) {
  return format(parseISO(dateStr), 'EEE, d MMM yyyy');
}

function formatTime(t) {
  return t.slice(0, 5);
}

function groupByWeek(shifts) {
  const groups = {};
  for (const shift of shifts) {
    const d = parseISO(shift.date);
    const weekNum = getISOWeek(d);
    const year = format(d, 'yyyy');
    const key = `${year}-W${String(weekNum).padStart(2, '0')}`;
    if (!groups[key]) groups[key] = { label: `Week ${weekNum} · ${year}`, shifts: [] };
    groups[key].shifts.push(shift);
  }
  return Object.values(groups);
}

// Month (YYYY-MM) before the current one in Europe/Berlin
function previousBerlinMonth() {
  const [y, m] = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date()).split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

// Own sick reports of this and last month, with the sick note (AU) toggle.
// Needed here because a covered sick shift moves to the colleague in the roster.
function MySickReports({ records, onChange }) {
  if (!records.length) return null;
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '12px 16px' }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
        <Thermometer size={16} style={{ color: 'var(--primary-dark)' }} /> My sick reports
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {records.map((r) => (
          <div key={r.id}>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: '6px' }}>
              {formatDate(r.date)} · {r.template_name || 'Shift'} {formatTime(r.start_time)}–{formatTime(r.end_time)}
            </div>
            <SickNoteToggle record={r} onChange={(upd) => onChange({ ...r, ...upd })} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MyShiftsPage() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coverConfirm, setCoverConfirm] = useState(null); // { shift, actionType: 'swap'|'sick' }
  const [coverSaving, setCoverSaving] = useState(false);

  const [freeWeekends, setFreeWeekends] = useState([]);
  const [sickReports, setSickReports] = useState([]);

  const loadSickReports = useCallback(() => {
    const from = `${previousBerlinMonth()}-01`;
    api.get('/sickness/me')
      .then((res) => setSickReports((res.data || []).filter((r) => r.date && r.date >= from)))
      .catch(() => setSickReports([]));
  }, []);

  useEffect(() => {
    loadSickReports();
    api.get('/shifts/my-shifts')
      .then((res) => setShifts(res.data))
      .catch(() => toast.error('Failed to load shifts.'))
      .finally(() => setLoading(false));
    api.get('/shifts/my-weekends')
      .then((res) => setFreeWeekends((res.data || []).filter((p) => p.free_weekends.length > 0)))
      .catch(() => setFreeWeekends([]));
  }, [loadSickReports]);

  const handleCoverRequest = useCallback(async (shift, actionType) => {
    setCoverSaving(true);
    setCoverConfirm(null);
    try {
      if (actionType === 'sick') {
        await api.post('/sickness', { shift_slot_id: shift.id });
        toast.success('Sick leave reported. Team has been notified.');
        loadSickReports();
      } else {
        await api.post('/shift-covers', { shift_slot_id: shift.id });
        toast.success('Cover request created. Team has been notified.');
      }
      // Refresh shifts list to reflect any re-assignment
      const res = await api.get('/shifts/my-shifts');
      setShifts(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Request failed.');
    } finally {
      setCoverSaving(false);
    }
  }, [loadSickReports]);

  const groups = groupByWeek(shifts);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Shifts</h1>
          <p className="page-subtitle">Your upcoming assigned shifts from published rosters.</p>
        </div>
      </div>

      {freeWeekends.map((p) => (
        <div
          key={p.period_id}
          className="card"
          style={{
            marginBottom: '16px', padding: '12px 16px', display: 'flex', gap: '10px', alignItems: 'flex-start',
            background: 'var(--primary-xlight)', border: '1px solid var(--primary)',
          }}
        >
          <CalendarCheck size={18} style={{ color: 'var(--primary-dark)', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '0.88rem' }}>
            <div style={{ fontWeight: 600 }}>Your free weekends (Rhythm {p.rhythm})</div>
            <div>{formatWeekends(p.free_weekends)}</div>
            {freeWeekends.length > 1 && (
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                Plan {formatDate(p.start_date)} – {formatDate(p.end_date)}
              </div>
            )}
          </div>
        </div>
      ))}

      <MySickReports
        records={sickReports}
        onChange={(upd) => setSickReports((list) => list.map((r) => (r.id === upd.id ? upd : r)))}
      />

      {loading ? (
        <LoadingSpinner label="Loading shifts…" />
      ) : shifts.length === 0 ? (
        <div className="empty-state">
          <CalendarCheck className="empty-state-icon" />
          <h3>No upcoming shifts</h3>
          <p>You have no shifts scheduled from published rosters.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {groups.map((group) => (
            <div key={group.label}>
              <h3
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '10px',
                }}
              >
                {group.label}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {group.shifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="card"
                    style={{
                      padding: '16px 20px',
                      borderLeft: '4px solid var(--primary-dark)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Date */}
                    <div style={{ minWidth: '140px' }}>
                      <p style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {formatDate(shift.date)}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: 'var(--text-light)',
                          fontSize: '0.85rem',
                          marginTop: '4px',
                        }}
                      >
                        <Clock size={13} />
                        {formatTime(shift.start_time)} – {formatTime(shift.end_time)}
                      </div>
                    </div>

                    {/* Shift name */}
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 500, color: 'var(--primary-dark)' }}>
                        {shift.template_name || 'Shift'}
                      </p>
                      {shift.is_training ? (
                        <p style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem', color: 'var(--text)', marginTop: '3px' }}>
                          <WhistleIcon size={14} />
                          {TRAINING_LABEL}
                        </p>
                      ) : null}
                      {shift.notes && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '4px',
                            color: 'var(--text-light)',
                            fontSize: '0.82rem',
                            marginTop: '4px',
                          }}
                        >
                          <FileText size={13} style={{ marginTop: '2px', flexShrink: 0 }} />
                          {shift.notes}
                        </div>
                      )}
                    </div>

                    {/* Cover request buttons */}
                    <div style={{ display: 'flex', gap: '6px', alignSelf: 'center', flexShrink: 0 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setCoverConfirm({ shift, actionType: 'swap' })}
                        title="Request shift cover"
                      >
                        <ArrowLeftRight size={13} />
                        Give away
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setCoverConfirm({ shift, actionType: 'sick' })}
                        title="Report sick leave"
                      >
                        <Thermometer size={13} />
                        I'm sick
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cover confirm modal */}
      {coverConfirm && (
        <Modal
          isOpen
          onClose={() => setCoverConfirm(null)}
          title={coverConfirm.actionType === 'sick' ? 'Report sick leave' : 'Request shift cover'}
          size="sm"
        >
          <p style={{ marginBottom: '8px' }}>
            <strong>{coverConfirm.shift.template_name || 'Shift'}</strong> on{' '}
            <strong>{formatDate(coverConfirm.shift.date)}</strong>
            {' '}({formatTime(coverConfirm.shift.start_time)} – {formatTime(coverConfirm.shift.end_time)})
          </p>
          {coverConfirm.actionType === 'sick' ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--danger)', background: 'var(--danger-light)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
              This sends an <strong>urgent notification</strong> to the entire team. The first person to respond will be assigned your shift.
            </p>
          ) : (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'var(--bg)', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              This sends a low-priority notification to all team members. They can take the shift or offer a swap.
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button className="btn btn-secondary" onClick={() => setCoverConfirm(null)} disabled={coverSaving}>
              Cancel
            </button>
            <button
              className={`btn ${coverConfirm.actionType === 'sick' ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => handleCoverRequest(coverConfirm.shift, coverConfirm.actionType)}
              disabled={coverSaving}
            >
              {coverSaving ? 'Submitting…' : coverConfirm.actionType === 'sick' ? 'Report sick' : 'Request cover'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default MyShiftsPage;
